import { useInfiniteQuery } from '@tanstack/react-query';
import { endOfDay, startOfDay } from 'date-fns';
import { DateRange } from 'react-day-picker';

export type FinancialReport = {
  id: number;
  external_id: string;
  symbol: string;
  title: string;
  published_date: string;
  filing_category: string | null;
  pdf_urls: string[];
  raw_metadata: ({
    pdf_link_labels?: string[];
  } & Record<string, unknown>) | null;
  created_at: string;
};

export type ReportFilters = {
  symbol?: string;
  dateRange?: DateRange;
};

const PAGE_SIZE = 20;

export function useReports(filters: ReportFilters) {
  const from = filters.dateRange?.from
    ? startOfDay(filters.dateRange.from).getTime()
    : undefined;
  const to = filters.dateRange?.to
    ? endOfDay(filters.dateRange.to).getTime()
    : undefined;

  const query = useInfiniteQuery<{ reports: FinancialReport[] }>({
    queryKey: ['/api/reports', filters],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(pageParam),
      });

      if (filters.symbol) {
        params.set('symbol', filters.symbol);
      }

      if (from !== undefined) {
        params.set('from', String(from));
      }

      if (to !== undefined) {
        params.set('to', String(to));
      }

      const response = await fetch(`/api/reports?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`${response.status}: ${response.statusText}`);
      }
      return response.json();
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.reports.length === PAGE_SIZE
        ? allPages.length * PAGE_SIZE
        : undefined;
    },
    initialPageParam: 0,
  });

  const reports = query.data?.pages.flatMap((page) => page.reports) ?? [];

  return {
    reports,
    isLoading: query.isLoading,
    fetchNextPage: query.fetchNextPage,
    hasMore: query.hasNextPage ?? false,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
